import {
  Button,
  Modal,
  SectionCard,
  Toggle as CanonicalToggle,
} from "@a2z/ui";
import { type ReactNode } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

// ── CSS-var aliases ───────────────────────────────────────────────────────
// The design source uses short variable names (--accent, --bg, etc).
// The seller app uses fully-qualified names. We mirror the design
// inline-style approach 1:1 by referencing the seller app's variables
// directly, but expose a tiny token map so the panel files stay readable.
export const tokens = {
  navy: "var(--color-hero-surface)",
  accent: "var(--accent-500)",
  accent2: "var(--accent-600)",
  bg: "var(--canvas)",
  bgSoft: "var(--surface-alt)",
  surface: "var(--surface)",
  border: "var(--border-light)",
  borderStrong: "var(--border-strong)",
  text: "var(--text-primary)",
  text2: "var(--text-secondary)",
  text3: "var(--text-muted)",
  danger: "var(--error-500)",
  success: "var(--success-500)",
  successSoft: "var(--success-100)",
  // Success text on a soft-success background. Theme-aware: the light theme
  // darkens the green, the dark theme keeps it light (see --success-text).
  successText: "var(--success-text)",
  info: "var(--primary-500)",
} as const;

// ── Inline icon factory ───────────────────────────────────────────────────
function Icon({
  d,
  size = 16,
  stroke = 1.6,
  fill = "none",
  color = "currentColor",
}: {
  d: ReactNode;
  size?: number;
  stroke?: number;
  fill?: string;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {typeof d === "string" ? <path d={d} /> : d}
    </svg>
  );
}

export const Icons = {
  back: <Icon d="M15 18l-6-6 6-6" />,
  search: (
    <Icon
      d={
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </>
      }
    />
  ),
  plus: <Icon d="M12 5v14M5 12h14" />,
  eye: (
    <Icon
      d={
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
          <circle cx="12" cy="12" r="3" />
        </>
      }
    />
  ),
  trash: (
    <Icon
      d={
        <>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        </>
      }
    />
  ),
  copy: (
    <Icon
      d={
        <>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </>
      }
    />
  ),
  edit: (
    <Icon
      d={
        <>
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </>
      }
    />
  ),
  close: (
    <Icon
      size={18}
      d={
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      }
    />
  ),
  globe: (
    <Icon
      d={
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
        </>
      }
    />
  ),
  card: (
    <Icon
      d={
        <>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </>
      }
    />
  ),
  truck: (
    <Icon
      d={
        <>
          <path d="M1 3h15v13H1z" />
          <path d="M16 8h4l3 3v5h-7" />
          <circle cx="5.5" cy="18.5" r="2" />
          <circle cx="18.5" cy="18.5" r="2" />
        </>
      }
    />
  ),
  doc: (
    <Icon
      d={
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="15" y2="17" />
        </>
      }
    />
  ),
  grid: (
    <Icon
      d={
        <>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </>
      }
    />
  ),
  check: <Icon d="M20 6L9 17l-5-5" stroke={2.3} size={22} />,
  team: (
    <Icon
      d={
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      }
    />
  ),
  mail: (
    <Icon
      d={
        <>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M22 6l-10 7L2 6" />
        </>
      }
    />
  ),
  shield: (
    <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  ),
  link: (
    <Icon
      d={
        <>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </>
      }
    />
  ),
};

// ── Section card ──────────────────────────────────────────────────────────
// Thin wrapper around the canonical @a2z/ui SectionCard. Preserved as a
// separate export so the ~15 redesign panel files keep their existing
// `title` / `desc` / `action` contract without churn.
export function Card({
  title,
  desc,
  children,
  action,
}: {
  title: ReactNode;
  desc?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <SectionCard title={title} subtitle={desc} right={action}>
      {children}
    </SectionCard>
  );
}

export function Label({ children }: { children: ReactNode }) {
  // Mirrors the FormField label markup so a standalone Label looks identical
  // to a Field-rendered label. Used when a panel needs the label text without
  // the input being a child of the same <label> element.
  return (
    <span className="block text-[12px] font-medium leading-tight text-[color:var(--label-ink)]">
      {children}
    </span>
  );
}

// Thin wrapper around canonical @a2z/ui Toggle. Preserved as `Toggle` (with
// the `on` prop name) so the redesign panel call sites compile unchanged.
export function Toggle({
  on,
  onChange,
}: {
  on?: boolean;
  onChange?: (next: boolean) => void;
}) {
  return <CanonicalToggle checked={on} onChange={onChange} size="md" />;
}

// inputStyle removed — all consumers migrated to <TextInput /> / <TextArea />
// / <SelectInput /> from @a2z/ui (see ../../forms/FormField.tsx).

// ── Reusable modal shell ──────────────────────────────────────────────────
// Thin wrapper around the canonical @a2z/ui Modal. Preserved as a separate
// export so the 5 redesign-modal call sites (add-payment, shipping-rule,
// free-shipping, integration, policy) keep their `valid` / `saveLabel` /
// `saveIcon` / `footerHint` / `maxWidth` contract without change.
export function ModalShell({
  icon,
  title,
  subtitle,
  onClose,
  children,
  footerHint,
  onSave,
  valid,
  saveLabel,
  saveIcon,
  maxWidth = 720,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footerHint?: ReactNode;
  onSave: () => void;
  valid: boolean;
  saveLabel: ReactNode;
  saveIcon?: boolean;
  maxWidth?: number;
}) {
  const t = useTranslations();
  const size: "md" | "lg" | "xl" | "2xl" =
    maxWidth >= 1000
      ? "2xl"
      : maxWidth >= 880
        ? "xl"
        : maxWidth >= 640
          ? "lg"
          : "md";

  return (
    <Modal
      isOpen
      onClose={onClose}
      size={size}
      icon={icon}
      title={title}
      subtitle={subtitle}
      footerLeft={footerHint}
      footerActions={
        <>
          <Button variant="outline" size="md" onClick={onClose}>
            {t(keys.common.cancel)}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={onSave}
            disabled={!valid}
            leadingIcon={
              saveIcon ? (
                <span style={{ fontSize: 13, fontWeight: 700 }}>+</span>
              ) : undefined
            }
          >
            {saveLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}

// ── Reusable governorate multiselect grid ────────────────────────────────
export function GovernorateGrid({
  governorates,
  selected,
  onToggle,
  disabledCovered = new Set<string>(),
  height = 280,
}: {
  governorates: string[];
  selected: Set<string>;
  onToggle: (g: string) => void;
  disabledCovered?: Set<string>;
  height?: number;
}) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3"
      style={{
        gap: 6,
        maxHeight: height,
        overflowY: "auto",
        padding: 4,
        border: `1px solid ${tokens.border}`,
        borderRadius: 10,
        background: tokens.bgSoft,
      }}
    >
      {governorates.map((g) => {
        const sel = selected.has(g);
        const covered = disabledCovered.has(g) && !sel;
        return (
          <button
            key={g}
            type="button"
            disabled={covered}
            onClick={() => onToggle(g)}
            style={{
              padding: "8px 10px",
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 500,
              textAlign: "start",
              border: sel
                ? `1.5px solid ${tokens.accent}`
                : `1px solid ${tokens.border}`,
              background: sel
                ? `color-mix(in oklab, ${tokens.accent} 10%, white)`
                : covered
                  ? "transparent"
                  : tokens.surface,
              color: covered
                ? tokens.text3
                : sel
                  ? tokens.accent2
                  : tokens.text,
              cursor: covered ? "not-allowed" : "pointer",
              opacity: covered ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 4,
                flexShrink: 0,
                border: `1.5px solid ${sel ? tokens.accent : tokens.borderStrong}`,
                background: sel ? tokens.accent : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0b1220",
                fontSize: 9,
                fontWeight: 800,
              }}
            >
              {sel ? "✓" : ""}
            </span>
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {g}
            </span>
          </button>
        );
      })}
    </div>
  );
}
